import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { Send, Bot, User, Loader2, Crown, X, Mic, MicOff } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useChat } from '../../contexts/ChatContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import Speech from './Speech';

const Chat = () => {
  const navigate = useNavigate();

  const [input, setInput] = useState('');
  // const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { messages, sendMessage, sendMessageNoStreaming, messageLimitInfo, voiceLimitInfo, updateTimeUsedFunction, startVoiceChat,
    stopVoiceChat,
    showSpinner,
    isRecording, } = useChat();
  const [timeLimit, setTimeLimit] = useState(0)

  // const [showSpinner, setShowSpinner] = useState(false);
  const [showText, setShowText] = useState(false);
  const [isPremium, setIsPremium] = useState(false)
  const [connectTime, setConnectTime] = useState<number | null>(null);
  const [disconnectTime, setDisconnectTime] = useState<number | null>(null);
  const [isMicOn, setIsMicOn] = useState(true);
  // "sk_d08798b4df3edc89be365c1c752b4294e1fecf6214d9fc67"
  // Timer for showing text message
  useEffect(() => {
    if (showSpinner) {
      setShowText(true);  // Show "Talk to Clara" message immediately when spinner shows

      const timer = setTimeout(() => {
        setShowText(false);  // Hide the text after 1 second
      }, 1000);

      return () => clearTimeout(timer); // Cleanup timeout on unmount or change of state
    }
  }, [showSpinner]);


  // console.log('messages', messages)
  const { user } = useAuth();

  const handleUpdateTimeLimit = async () => {

    try {
      if (user?.email) {
        const info = await api.updateTimeUsed(timeLimit);
        console.log(info, "infotime limit")

      }
    } catch (error) {
      console.error("Error updating message limit:", error);
      toast.error('Failed to update message limit');
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const message = input.trim();
    setInput('');
    await sendMessage(message);
  };

  const handleUpgradeClick = () => {
    navigate('/pricing');
  };


  const getSubcription = async () => {
    try {

      const data: any = await api.updateSubscriptionFromStripe()
      if (!data) {
        const subscriptionData: any = await api.getSubscription()
        console.log('subscriptionData?.is_premium', subscriptionData)
        return;
      }
      else {
        const subscriptionData: any = await api.getSubscription()
        console.log('subscriptionData?.is_premium', subscriptionData?.is_premium)
        if (subscriptionData?.data?.is_premium) {
          setIsPremium(true)
        }
        return;
      }
    } catch (err) {
      const subscriptionData: any = await api.getSubscription()
      console.log('subscriptionData', subscriptionData?.data)
    }
  }

  useEffect(() => {
    getSubcription();
  }, []);

  const requestMicrophoneAccess = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      console.error("Microphone access denied:", error);
      toast.error("Microphone access denied. Please enable it in Settings > Safari > Microphone.");
    }
  };

  useEffect(() => {
    requestMicrophoneAccess();
  }, []);








  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Messages */}


      <div className="pt-2 pb-[150px] h-full   overflow-y-scroll">
        <div className="max-w-4xl mx-auto space-y-4">
          <AnimatePresence initial={false}>

            {messages.map((message) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className={cn(
                  "flex items-start space-x-4",
                  message.role === 'assistant' ? 'justify-start' : 'justify-end'
                )}
              >
                {message.role === 'assistant' && (
                  <div className="p-2 rounded-lg bg-violet-500/20">
                    <Bot className="w-6 h-6 text-violet-400" />
                  </div>
                )}
                <div
                  className={cn(
                    "max-w-[80%] rounded-2xl px-4 py-2",
                    message.role === 'assistant'
                      ? "bg-white/5 text-white"
                      : "bg-violet-500 text-white"
                  )}
                >
                  <p className="whitespace-pre-wrap">{message.content}</p>
                </div>
                {message.role === 'user' && (
                  <div className="p-2 rounded-lg bg-violet-600">
                    <User className="w-6 h-6 text-white" />
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Message limit info */}
      <div className='bottom-0 absolute w-full'>


        {messageLimitInfo && !messageLimitInfo.isPremium && (
          <div className="border-t border-white/10 bg-white/5 backdrop-blur-sm px-4 py-2 ">
            <div className="max-w-4xl mx-auto flex items-center justify-between">
              <div className="flex items-center space-x-2 text-sm text-gray-400">
                <span>
                  {messageLimitInfo.used} / {messageLimitInfo.limit} messages used today
                </span>
                {!messageLimitInfo.isPremium && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleUpgradeClick}
                    className="px-3 py-1 rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 text-white flex items-center space-x-1"
                  >
                    <Crown className="w-4 h-4" />
                    <span>Upgrade</span>
                  </motion.button>
                )}
              </div>
              {messageLimitInfo.used >= messageLimitInfo.limit && (
                <span className="text-sm text-red-400">
                  Daily limit reached
                </span>
              )}
            </div>
          </div>
        )}

        {showSpinner && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 inset-0 bg-gradient-to-br from-[#3a007e] via-[#7F00FF] to-[#3a007e] flex flex-col items-center justify-center z-[999]"
          >

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute text-center text-4xl text-white/70 font-bold top-40  "
            >
              I'm listening...
            </motion.div>

            <AnimatedCircle />

            {showText && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="relative text-center text-2xl text-black font-bold "
              >
                Talk to Clara
              </motion.div>
            )}

            {/* Bottom Buttons */}
            <div className="absolute bottom-10 flex gap-4">
              {/* <button
                onClick={handleMicToggle}
                className="px-4 py-2 rounded-full bg-black text-white font-medium hover:scale-105 transition"
              >
                {isMicOn ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
              </button> */}

              <button
                onClick={stopVoiceChat}
                className="px-4 py-2 rounded-full bg-red-500 text-white font-medium hover:scale-105 transition"
              >
                <X />
              </button>
            </div>
          </motion.div>
        )}



        {/* Input */}
        <div className=" w-full  border-t border-white/10 bg-white/5 backdrop-blur-sm p-4">
          <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
            <div className="relative flex items-center">

              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                className={cn(
                  "w-full px-4 py-3 pr-12 rounded-xl",
                  "bg-white/5 border border-white/10",
                  "text-white placeholder-gray-400",
                  "focus:outline-none focus:ring-2 focus:ring-violet-500/50",
                  loading && "opacity-50 cursor-not-allowed"
                )}
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className={cn(
                  "absolute right-12 top-1/2 -translate-y-1/2",
                  "p-2 rounded-lg",
                  "text-gray-400 hover:text-white",
                  "transition-colors duration-200",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {loading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <Send className="w-6 h-6" />
                )}
              </button>
              <button
                type="button"
                onClick={isRecording ? stopVoiceChat : startVoiceChat}
                className={cn(
                  "absolute right-2 top-1/2 -translate-y-1/2",
                  "p-2 rounded-lg",
                  isRecording ? "text-red-400" : "text-gray-400 hover:text-white",
                  "transition-colors duration-200"
                )}
              >
                {isRecording ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
              </button>
              {/* <Speech /> */}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Chat;


const AnimatedCircle = () => {
  const controls = useAnimation();

  useEffect(() => {
    const runAnimation = async () => {
      // Step 1: Scale from 0.2 to 1 over 1 second
      await controls.start({
        scale: 1,
        transition: { duration: 1, ease: "easeOut" },
      });

      // Step 2: Start pulsing animation
      controls.start({
        scale: [1, 1.1, 1],
        transition: {
          duration: 1.5,
          repeat: Infinity,
          ease: "easeInOut",
        },
      });
    };

    runAnimation();
  }, [controls]);

  return (
    <motion.div
      initial={{ scale: 0.2 }} // 20% of actual size (like 20px)
      animate={controls}
      className="relative w-[100px] h-[100px] rounded-full bg-[ #7F00FF] border-4 border-[#00FFE7] shadow-[0_0_40px_#00FFE7]"
    />
  );
};
